import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { AttachMoney } from '@mui/icons-material'
import { ContentCopy } from 'mdi-material-ui'
import { inject } from 'mobx-react'
import moment from 'moment'
import React from 'react'
import { ICMS1500, ILocation } from 'src/Definitions'
import { IStores } from 'src/Stores'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { getPaidContractAmountHistory } from 'src/services/LookupService'
import { formatCurrency } from 'src/utils/Formatter'
import { ClaimLineItemLookupRequest } from 'src/viewModels/ClaimLineItemLookupRequest'
import { PaidClaimLineItemSummaryResult } from 'src/viewModels/PaidClaimLineItemSummaryResult'
import { IClaimFormValues } from './ClaimFormValues'
import ReactPaginate from 'react-paginate'

interface ILineItemContractModalProps {
  close?: () => void
  isOpen?: boolean
  index?: number
  selectedLocation?: ILocation
  selectedCMS1500: IClaimFormValues | ICMS1500
  hideButton?: boolean
}

class LineItemContractModal extends React.Component<ILineItemContractModalProps> {
  public state = {
    gridData: new Array<PaidClaimLineItemSummaryResult>(),
    pageSize: 5,
    page: 1,
    pageCount: 1,
    firstRecordIndex: 0,
    lastRecordIndex: 0,
    totalItems: 0,
    loading: true,
  }

  public async componentDidMount(): Promise<void> {
    this.getData()
  }

  public async getData() {
    this.setState({ loading: true })
    if (
      this.props.selectedCMS1500.providerTypeId &&
      this.props.selectedLocation != undefined &&
      this.props.index != undefined &&
      this.props.selectedCMS1500.q24 != undefined &&
      this.props.selectedLocation?.provider?.name != 'UNKNOWN'
    ) {
      var data = new ClaimLineItemLookupRequest()
      data.providerGroupId = this.props.selectedLocation!.providerId
      data.cptCode =
        this.props.selectedCMS1500.q24?.at(this.props.index!)?.dProcedureCptHcpcs ?? ''
      data.providerTypeId = this.props.selectedCMS1500.providerTypeId
      data.page = this.state.page
      data.pageSize = this.state.pageSize
      await getPaidContractAmountHistory(data).then((response: any) => {
        this.setState(
          {
            pageCount: response.totalPages,
            totalItems: response.totalItems,
            gridData: response.items,
            loading: false,
          },
          () => {
            this.setPaginationOffsetData()
          }
        )
      })
    } else {
      this.setState({ loading: false })
    }
  }

  private close = () => {
    this.props.close!()
  }

  private copy = (paidAmount: number) => {
    var lineItem = this.props.selectedCMS1500.q24?.at(this.props.index!)

    if (lineItem) {
      lineItem.contractAmount = paidAmount.toFixed(2)
    }

    this.close()
  }

  private isGridDataEmpty = () => {
    return this.state.totalItems === 0
  }

  private setPaginationOffsetData = () => {
    const { page, totalItems, pageSize } = this.state
    const firstRecordIndex = (page - 1) * pageSize + 1
    const lastRecordIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    this.setState({
      firstRecordIndex: firstRecordIndex,
      lastRecordIndex: lastRecordIndex,
    })
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ page: page + 1 }, () => this.getData())
  }

  private paginationInfo = () => {
    if (!this.isGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }

  private mapLogGridData = () => {
    if (this.isGridDataEmpty()) {
      return (
        <TableRow className="gridNoPadding" key={1}>
          <TableCell colSpan={10} align="center">
            No previous payments
          </TableCell>
        </TableRow>
      )
    } else {
      return this.state.gridData.map((record, index) => (
        <TableRow className="gridPadding" key={index}>
          <TableCell>
            {record.episodeOfCareNumber}-{record.groupNumber}
          </TableCell>
          <TableCell>{record.placeOfService}</TableCell>
          <TableCell>{record.cptCode}</TableCell>
          <TableCell>{record.modifiers}</TableCell>
          <TableCell align="right">{record.quantity}</TableCell>
          <TableCell align="right">
            {moment(record.datePaid).format('MM/DD/YYYY')}
          </TableCell>
          <TableCell align="right">
            {formatCurrency(record.amountPaid.toString())}
          </TableCell>
          {this.props.hideButton ? (
            <></>
          ) : (
            <TableCell>
              <ContentCopy
                color="primary"
                style={{ cursor: 'pointer' }}
                onClick={() => this.copy(record.amountPaid)}
              />
            </TableCell>
          )}
        </TableRow>
      ))
    }
  }

  public render() {
    const { isOpen, index } = this.props

    return (
      <Dialog key={index} fullWidth={true} maxWidth="md" open={isOpen!}>
        <DialogTitle style={{ padding: 0 }}>
          <ResponsiveAppBar
            title={`Paid Contract Amount History:  ${this.props.selectedLocation?.provider?.name}`}
            pageIcon={<AttachMoney />}
            isModal={true}
          />
        </DialogTitle>
        {this.state.loading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <DialogContent>
          {this.props.selectedLocation?.provider?.name == 'UNKNOWN' ? (
            <Grid xs={12}>
              <Typography
                noWrap
                style={{ textAlign: 'center', paddingTop: '10px' }}
                color="error"
              >
                Provider Group is unknown, pricing information is not available
              </Typography>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow className="gridPadding">
                    <TableCell>EOC-Group#</TableCell>
                    <TableCell>Place of Service</TableCell>
                    <TableCell>CPT</TableCell>
                    <TableCell>Modifiers</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Date Paid</TableCell>
                    <TableCell align="right">Amount Paid</TableCell>
                    {this.props.hideButton ? <></> : <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: '100%' }}>{this.mapLogGridData()}</TableBody>
              </Table>
              <div className="pagination-row">
                {this.paginationInfo()}
                <ReactPaginate
                  previousLabel={'<'}
                  nextLabel={'>'}
                  onPageChange={this.handlePageChange}
                  forcePage={this.state.page - 1}
                  pageCount={this.state.pageCount}
                  containerClassName={'pagination'}
                  activeClassName={'active'}
                  //@ts-ignore
                  renderOnZeroPageCount={null}
                />
              </div>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.close} data-cy="close">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
}

const InjectedLineItemContractModal = inject<
  IStores,
  ILineItemContractModalProps,
  Partial<ILineItemContractModalProps>,
  any
>((stores: IStores) => ({
  close: stores.cms1500.closeContractModal,
  isOpen: stores.cms1500.contractModalIsOpen,
  index: stores.cms1500.contractModalIndex,
  selectedLocation: stores.locations.selectedEditClaimLocation,
}))(LineItemContractModal)

export default InjectedLineItemContractModal
